// Duty List (2026-09-14) - web version of the WinForms "Duty List" report
// (Texas_Admin frm_assignment_details). GET /attendance/duty-list returns one
// branch-month: every slot and, per slot, one guard code per day for each
// shift (Batch171). Everything the page shows - Detail grid, Summary,
// Guards, New Assignments, Blank - is derived here from that one response,
// so switching views or filters never goes back to the server.

// The web holds the branch duty list only up to this day (the one-time copy,
// HistoricalDutyList). After it, only duty marked on the web shows - the
// user chose "web punches only" on 2026-09-14 - so later days of a month
// read as blank even where the Windows app has them.
export const HISTORY_LAST_DAY = "2026-09-02";

const parseDate = (v) => (v ? new Date(`${String(v).slice(0, 10)}T00:00:00`) : null);
const monthShort = (data) => new Date(data.year, data.month - 1, 1).toLocaleDateString("en-GB", { month: "short" });
const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

export const monthLabel = (year, month) =>
  new Date(year, month - 1, 1).toLocaleDateString("en-GB", { month: "long", year: "numeric" });

export const shortDate = (v) =>
  v ? parseDate(v).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "";

// The last day of the month that can have duty yet: the whole month in the
// past, up to today in the current month, none in a future month.
export const lastElapsedDay = (data) => {
  const today = parseDate(data.today);
  const y = today.getFullYear();
  const m = today.getMonth() + 1;
  if (data.year < y || (data.year === y && data.month < m)) return data.daysInMonth;
  if (data.year === y && data.month === m) return today.getDate();
  return 0;
};

// Days of the month (1-based, inclusive) inside the slot's own active dates.
const slotWindow = (slot, data) => {
  const monthStart = new Date(data.year, data.month - 1, 1);
  const monthEnd = new Date(data.year, data.month - 1, data.daysInMonth);
  const from = parseDate(slot.fromDate);
  const to = parseDate(slot.toDate);
  if ((from && from > monthEnd) || (to && to < monthStart)) return null;
  const start = from && from > monthStart ? from.getDate() : 1;
  const end = to && to < monthEnd ? to.getDate() : data.daysInMonth;
  return { start, end };
};

/**
 * One row per slot and shift. A slot normally has one shift (its own
 * day_night); if duty was also recorded on the other shift, that gets a
 * second row flagged offShift so nothing recorded is hidden.
 *
 * expected[d] = the slot should have had someone on day d (active, within
 * its dates, already elapsed). A switched-off slot (listed only because it
 * was worked) and an off-shift row expect only the days that were filled.
 */
export const buildRows = (data) => {
  if (!data) return [];
  const n = data.daysInMonth;
  const lastDay = lastElapsedDay(data);
  const rows = [];

  data.slots.forEach((slot) => {
    const own = slot.dayNight === "Night" ? "Night" : "Day";
    const other = own === "Day" ? "Night" : "Day";
    const arrays = { Day: slot.day, Night: slot.night };
    const shifts = [own];
    if (arrays[other]?.some((c) => c != null)) shifts.push(other);

    shifts.forEach((shift) => {
      const codes = arrays[shift] || new Array(n).fill(null);
      const offShift = shift !== own;
      const web = {};
      (slot.web || []).filter((w) => w.s === shift).forEach((w) => { web[w.d] = w.t || ""; });
      const extra = {};
      (slot.extra || []).filter((x) => x.s === shift).forEach((x) => { extra[x.d] = [...(extra[x.d] || []), x.e]; });

      const win = slotWindow(slot, data);
      const expected = new Array(n).fill(false);
      for (let d = 1; d <= n; d += 1) {
        const filled = codes[d - 1] != null;
        const inWindow = win && d >= win.start && d <= win.end && d <= lastDay;
        expected[d - 1] = filled || (slot.isActive && !offShift && !!inWindow);
      }
      const filledDays = codes.filter((c) => c != null).length;
      const expectedDays = expected.filter(Boolean).length;
      const coversWholeMonth = !!win && win.start === 1 && win.end >= lastDay && slot.isActive && !offShift;
      // Worked every day = full month, whatever the slot's flag says now: is_active is
      // TODAY's state, so a post switched off after the month (TALAB, Aug 2026) read
      // "Switched off - 31 days" under part month (2026-10-07).
      const fullMonth = lastDay > 0 && !offShift && filledDays >= lastDay;

      // Why a slot is not full month - the Summary sorts these into "the post
      // itself only ran part of the month" (temp, new, ended, switched off)
      // and "the post ran all month but days were not marked".
      const partTags = [];
      if (!fullMonth && !offShift && lastDay > 0) {
        const mon = monthShort(data);
        if (!slot.isActive) partTags.push({ kind: "off", label: "Switched off" });
        else if (win && !coversWholeMonth) {
          const range = `${win.start}-${Math.min(win.end, lastDay)} ${mon}`;
          if (slot.isTemp) partTags.push({ kind: "temp", label: `Temp ${range}` });
          else if (win.start > 1) partTags.push({ kind: "new", label: `New from ${win.start} ${mon}` });
          else partTags.push({ kind: "ended", label: `Ended ${win.end} ${mon}` });
        }
        if (expectedDays - filledDays > 0) partTags.push({ kind: "blank", label: `${plural(expectedDays - filledDays, "day")} not marked` });
      }

      rows.push({
        key: `${slot.custId}-${slot.assignmentId}-${shift}`,
        custId: slot.custId,
        custName: slot.custName || `Customer ${slot.custId}`,
        area: slot.area || "",
        assignmentId: slot.assignmentId,
        assignmentName: slot.assignmentName || `Assignment ${slot.assignmentId}`,
        shift,
        offShift,
        isActive: slot.isActive,
        isTemp: slot.isTemp,
        fromDate: slot.fromDate,
        toDate: slot.toDate,
        codes,
        web,
        extra,
        expected,
        filledDays,
        expectedDays,
        blankDays: expectedDays - filledDays,
        fullMonth,
        partTags,
      });
    });
  });
  return rows;
};

export const guardMap = (data) => {
  const map = new Map();
  (data?.guards || []).forEach((g) => map.set(Number(g.empCode), g));
  return map;
};

export const guardName = (guards, code) => guards.get(Number(code))?.name || "";

// Text search across customer, area, assignment and every guard on the row.
export const filterRows = (rows, guards, { custId, area, shift, search, hideEmpty, onlyTemp }) => {
  const term = (search || "").trim().toLowerCase();
  return rows.filter((r) => {
    if (custId != null && r.custId !== custId) return false;
    if (area && r.area !== area) return false;
    if (shift && r.shift !== shift) return false;
    if (hideEmpty && r.filledDays === 0) return false;
    if (onlyTemp && !r.isTemp) return false;
    if (!term) return true;
    if (`${r.custName} ${r.area} ${r.assignmentName}`.toLowerCase().includes(term)) return true;
    return r.codes.some(
      (c) => c != null && (String(c).includes(term) || guardName(guards, c).toLowerCase().includes(term))
    );
  });
};

export const groupByCustomer = (rows) => {
  const map = new Map();
  rows.forEach((r) => {
    if (!map.has(r.custId)) map.set(r.custId, { custId: r.custId, custName: r.custName, rows: [] });
    map.get(r.custId).rows.push(r);
  });
  return [...map.values()].sort((a, b) => a.custName.localeCompare(b.custName) || a.custId - b.custId);
};

/**
 * Per-customer summary - same idea as the WinForms Summary ("3 Day - Full
 * Month", "1 Night - 12") with the counts spelled out. Off-shift rows count
 * their shifts but not as a slot.
 *
 * Full month per shift reads "Yes" when every slot of that shift was
 * covered all month (what the client is invoiced on), otherwise "n of N".
 * partMonth lists the other slots WITH the reason, so a temp or new post is
 * told apart from a post whose guards were simply not marked:
 * "1 x Night - 10 days" + tags [Temp 12-21 Sep] or [3 days not marked].
 */
// "Yes" = every slot of the shift covered all month; "-" = no slot of it.
// 2026-10-09: "0 of 1 and 0 of 3 - don't understand". A bare "0 of 3" read like a score, so it
// now says what is counted: "No" when no post was manned every day, else "1 of 3 posts".
export const fullMonthText = (full, total) =>
  !total ? "-" : full === total ? "Yes" : full === 0 ? "No" : `${full} of ${total} posts`;

export const summarizeCustomers = (rows) =>
  groupByCustomer(rows).map((g) => {
    const slots = g.rows.filter((r) => !r.offShift);
    const part = new Map();
    slots
      .filter((r) => !r.fullMonth)
      .forEach((r) => {
        const tagText = r.partTags.map((t) => t.label).join(", ");
        const key = `${r.shift}|${r.filledDays}|${tagText}`;
        const entry = part.get(key) || { shift: r.shift, days: r.filledDays, tags: r.partTags, count: 0 };
        entry.count += 1;
        part.set(key, entry);
      });
    const partMonth = [...part.values()]
      .sort((a, b) => a.shift.localeCompare(b.shift) || b.days - a.days)
      .map((p) => {
        const text = `${p.count} x ${p.shift} - ${plural(p.days, "day")}`;
        return { text, tags: p.tags, full: p.tags.length ? `${text} (${p.tags.map((t) => t.label).join(", ")})` : text };
      });
    const daySlots = slots.filter((r) => r.shift === "Day").length;
    const nightSlots = slots.filter((r) => r.shift === "Night").length;
    const fullDaySlots = slots.filter((r) => r.fullMonth && r.shift === "Day").length;
    const fullNightSlots = slots.filter((r) => r.fullMonth && r.shift === "Night").length;
    const guardsSeen = new Set();
    g.rows.forEach((r) => r.codes.forEach((c) => c != null && guardsSeen.add(Number(c))));
    return {
      custId: g.custId,
      custName: g.custName,
      areas: [...new Set(g.rows.map((r) => r.area).filter(Boolean))].join(", "),
      daySlots,
      nightSlots,
      tempSlots: slots.filter((r) => r.isTemp).length,
      permSlots: slots.filter((r) => !r.isTemp).length,
      fullMonthSlots: slots.filter((r) => r.fullMonth).length,
      fullDaySlots,
      fullNightSlots,
      fullDayText: fullMonthText(fullDaySlots, daySlots),
      fullNightText: fullMonthText(fullNightSlots, nightSlots),
      partSlots: slots.filter((r) => !r.fullMonth).length,
      dayShifts: g.rows.filter((r) => r.shift === "Day").reduce((s, r) => s + r.filledDays, 0),
      nightShifts: g.rows.filter((r) => r.shift === "Night").reduce((s, r) => s + r.filledDays, 0),
      blankShifts: g.rows.reduce((s, r) => s + r.blankDays, 0),
      dueShifts: g.rows.reduce((s, r) => s + r.expectedDays, 0),
      guards: guardsSeen.size,
      partMonth,
      partMonthText: partMonth.map((p) => p.full).join(" | "),
    };
  });

export const totalsOf = (rows) => {
  const slots = rows.filter((r) => !r.offShift);
  const guardsSeen = new Set();
  rows.forEach((r) => r.codes.forEach((c) => c != null && guardsSeen.add(Number(c))));
  const partRows = slots.filter((r) => !r.fullMonth);
  return {
    customers: new Set(rows.map((r) => r.custId)).size,
    daySlots: slots.filter((r) => r.shift === "Day").length,
    nightSlots: slots.filter((r) => r.shift === "Night").length,
    fullMonthSlots: slots.length - partRows.length,
    tempSlots: slots.filter((r) => r.isTemp).length,
    permSlots: slots.filter((r) => !r.isTemp).length,
    partSlots: partRows.length,
    partShifts: partRows.reduce((s, r) => s + r.filledDays, 0),
    dayShifts: rows.filter((r) => r.shift === "Day").reduce((s, r) => s + r.filledDays, 0),
    nightShifts: rows.filter((r) => r.shift === "Night").reduce((s, r) => s + r.filledDays, 0),
    blankShifts: rows.reduce((s, r) => s + r.blankDays, 0),
    dueShifts: rows.reduce((s, r) => s + r.expectedDays, 0),
    guards: guardsSeen.size,
  };
};

/** Every guard on the (filtered) rows: shifts, customers, days worked. */
export const summarizeGuards = (rows, guards) => {
  const map = new Map();
  rows.forEach((r) => {
    r.codes.forEach((c, i) => {
      if (c == null) return;
      const code = Number(c);
      if (!map.has(code)) {
        map.set(code, {
          empCode: code,
          name: guardName(guards, code),
          employeeRecordGuid: guards.get(code)?.employeeRecordGuid,
          dayShifts: 0,
          nightShifts: 0,
          customers: new Set(),
          days: [],
        });
      }
      const g = map.get(code);
      if (r.shift === "Night") g.nightShifts += 1;
      else g.dayShifts += 1;
      g.customers.add(r.custName);
      g.days.push({ day: i + 1, shift: r.shift, custName: r.custName, assignmentName: r.assignmentName, area: r.area, web: r.web[i + 1] });
    });
  });
  return [...map.values()]
    .map((g) => ({
      ...g,
      total: g.dayShifts + g.nightShifts,
      customerCount: g.customers.size,
      customerList: [...g.customers].sort().join(", "),
      days: g.days.sort((a, b) => a.day - b.day || a.shift.localeCompare(b.shift)),
    }))
    .sort((a, b) => b.total - a.total || a.empCode - b.empCode);
};

/** Slots that started in the selected month (WinForms "New Assignments"). */
export const newAssignments = (rows, data) => {
  const first = new Date(data.year, data.month - 1, 1);
  const last = new Date(data.year, data.month - 1, data.daysInMonth);
  return rows
    .filter((r) => !r.offShift)
    .filter((r) => {
      const from = parseDate(r.fromDate);
      return from && from >= first && from <= last;
    })
    .sort((a, b) => String(a.fromDate).localeCompare(String(b.fromDate)) || a.custName.localeCompare(b.custName));
};

// "3-7, 9, 12" from [3,4,5,6,7,9,12].
export const compressDays = (days) => {
  const out = [];
  let i = 0;
  while (i < days.length) {
    let j = i;
    while (j + 1 < days.length && days[j + 1] === days[j] + 1) j += 1;
    out.push(j > i ? `${days[i]}-${days[j]}` : `${days[i]}`);
    i = j + 1;
  }
  return out.join(", ");
};

/**
 * Slots left without a guard (WinForms "Show Blank"): expected days with
 * nobody on them, either across the month or only in the last N days up
 * to today / month end.
 */
export const blankSlots = (rows, data, lastNDays) => {
  const lastDay = lastElapsedDay(data);
  const fromDay = lastNDays ? Math.max(1, lastDay - lastNDays + 1) : 1;
  return rows
    .map((r) => {
      const days = [];
      for (let d = fromDay; d <= lastDay; d += 1) {
        if (r.expected[d - 1] && r.codes[d - 1] == null) days.push(d);
      }
      return { ...r, blankInWindow: days, blankLabel: compressDays(days) };
    })
    .filter((r) => r.blankInWindow.length > 0)
    .sort((a, b) => a.custName.localeCompare(b.custName) || a.assignmentName.localeCompare(b.assignmentName));
};
